import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { zodResolver } from "@hookform/resolvers/zod";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  listClubCoachProfiles,
  type ClubCoachProfileDraft,
} from "@/libs/staffApi";
import {
  createProgram,
  PROGRAM_TYPES,
  type ProgramType,
  type UnifiedProgramCreate,
} from "@/libs/programsApi";
import { datetimeLocalToUtc, formatTimeRange } from "./program-detail-time";
const isProgramAuthoringLocator = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  ) &&
  value !== "00000000-0000-0000-0000-000000000000";
const sameProgramFacility = (left: unknown, right: unknown): boolean =>
  isProgramAuthoringLocator(left) &&
  isProgramAuthoringLocator(right) &&
  left.toLowerCase() === right.toLowerCase();

const PROGRAM_TYPE_LABELS: Record<ProgramType, string> = {
  group: "Group",
  private: "Private",
  camp: "Camp",
  clinic: "Clinic",
  league: "League",
  package: "Package",
};

const DURATION_ERROR = "Session length must be between 1 and 480 minutes.";
const RECURRENCE_OPTIONS = ["weekly", "daily", "once"] as const;

const programCreateSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required.").max(255),
    program_type: z.enum(PROGRAM_TYPES as [ProgramType, ...ProgramType[]]),
    description: z.string(),
    start_date: z.string().min(1, "Start date is required."),
    end_date: z.string(),
    recurrence: z.enum(RECURRENCE_OPTIONS),
    session_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Choose a session start time."),
    schedule_duration_minutes: z.string().refine((value) => {
      const duration = Number(value);
      return (
        Number.isFinite(duration) &&
        Number.isInteger(duration) &&
        duration >= 1 &&
        duration <= 480
      );
    }, DURATION_ERROR),
    coach_profile_id: z.string(),
    learning_space_enabled: z.boolean(),
  })
  .superRefine((values, context) => {
    if (values.recurrence !== "once" && !values.end_date) {
      context.addIssue({
        code: "custom",
        path: ["end_date"],
        message: "An end date is required for a repeating program.",
      });
    }
    if (
      values.recurrence === "once" ||
      !values.start_date ||
      !values.end_date
    ) {
      return;
    }
    const startTime = Date.parse(values.start_date);
    const endTime = Date.parse(values.end_date);
    if (
      Number.isFinite(startTime) &&
      Number.isFinite(endTime) &&
      endTime < startTime
    ) {
      context.addIssue({
        code: "custom",
        path: ["end_date"],
        message: "End date must be on or after the start date.",
      });
    }
  });

type ProgramCreateValues = z.infer<typeof programCreateSchema>;
type CreationPhase = "dispatched" | "confirmed" | "uncertain";
type CreationIntent = { phase: CreationPhase };

function confirmedNoEffectRejection(error: unknown): boolean {
  // This exact Program guard rejection is before DML and request-rolled-back.
  // Generic API/transport errors do not establish that a create had no effect.
  return (
    isApiError(error) &&
    error.status === 409 &&
    error.code === "COMMERCIAL_CONFLICT"
  );
}

const DEFAULT_VALUES: ProgramCreateValues = {
  name: "",
  program_type: "group",
  description: "",
  start_date: "",
  end_date: "",
  recurrence: "weekly",
  session_time: "18:00",
  schedule_duration_minutes: "60",
  coach_profile_id: "",
  learning_space_enabled: false,
};

type CoachOption = {
  profileId: string;
  label: string;
};

const NO_INSTRUCTOR_OPTION: CoachOption = {
  profileId: "",
  label: "No instructor",
};

function coachLabel(coach: ClubCoachProfileDraft): string {
  return coach.display_name?.trim() || "Coach";
}

function rruleUntil(instant: Date): string {
  return instant
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

function recurrenceRule(
  recurrence: ProgramCreateValues["recurrence"],
  finalSessionEnd: Date,
): string {
  if (recurrence === "once") return "FREQ=DAILY;COUNT=1";
  const frequency = recurrence === "daily" ? "DAILY" : "WEEKLY";
  return `FREQ=${frequency};UNTIL=${rruleUntil(finalSessionEnd)}`;
}

function sessionInstants(
  date: string,
  time: string,
  durationMinutes: number,
  timezone: string,
): { start: Date; end: Date } | null {
  const start = datetimeLocalToUtc(`${date}T${time}`, timezone);
  if (start === null || !Number.isFinite(durationMinutes)) return null;
  return {
    start,
    // Session duration is elapsed time, including across a DST transition.
    end: new Date(start.getTime() + durationMinutes * 60_000),
  };
}

function LoadingStatus({ label }: { label: string }) {
  return (
    <Stack
      role="status"
      aria-live="polite"
      direction="row"
      spacing={1}
      sx={{ minHeight: 88, alignItems: "center" }}
    >
      <CircularProgress size={20} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

export function ProgramCreate() {
  const selection = useResolvedFacility();
  const { user } = useAuth();
  return (
    <ProgramCreateForScope
      key={JSON.stringify([
        user?.uid,
        selection.facilityId,
        selection.facility?.organization_id,
      ])}
      selection={selection}
    />
  );
}

function ProgramCreateForScope({
  selection,
}: {
  selection: ReturnType<typeof useResolvedFacility>;
}) {
  const navigate = useNavigate();
  const { facility, facilityId, isLoading: facilityLoading } = selection;
  const verifiedFacility =
    !facilityLoading &&
    facility !== null &&
    Boolean(facilityId) &&
    facility.facility_id === facilityId
      ? facility
      : null;
  const verifiedFacilityId = verifiedFacility?.facility_id ?? null;
  const orgId = verifiedFacility?.organization_id ?? null;
  const submitInFlight = useRef(false);
  const creationIntent = useRef<CreationIntent | null>(null);
  const [creationPhase, setCreationPhase] = useState<CreationPhase | null>(
    null,
  );
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function holdCreation(owned: CreationIntent, phase: CreationPhase): void {
    if (creationIntent.current !== owned) return;
    owned.phase = phase;
    if (mounted.current) setCreationPhase(phase);
  }

  const form = useForm<ProgramCreateValues>({
    resolver: zodResolver(programCreateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const coachQuery = useQuery({
    queryKey: ["programs", "create", "coaches", verifiedFacilityId],
    queryFn: () => listClubCoachProfiles(orgId ?? ""),
    enabled: Boolean(verifiedFacilityId && orgId),
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: (body: UnifiedProgramCreate) => createProgram(body),
    retry: false,
  });

  const coachOptions = useMemo<CoachOption[]>(
    () => [
      NO_INSTRUCTOR_OPTION,
      ...(coachQuery.data ?? [])
        .filter(
          (coach) =>
            coach.status === "published" &&
            (coach.facilities ?? []).some(
              (binding) =>
                binding.facility_id === verifiedFacilityId &&
                binding.status === "active",
            ),
        )
        .map((coach) => ({
          profileId: coach.coach_profile_id,
          label: coachLabel(coach),
        })),
    ],
    [coachQuery.data, verifiedFacilityId],
  );

  useEffect(() => {
    form.setValue("coach_profile_id", "");
  }, [form, verifiedFacilityId]);

  useEffect(() => {
    if (coachQuery.isError) {
      form.setValue("coach_profile_id", "");
    }
  }, [coachQuery.isError, form, verifiedFacilityId]);

  const isCreating = createMutation.isPending || submitInFlight.current;
  const coachReadInProgress = coachQuery.isPending || coachQuery.isFetching;
  const coachRequestId = isApiError(coachQuery.error)
    ? coachQuery.error.requestId
    : undefined;
  const createRequestId = isApiError(createMutation.error)
    ? createMutation.error.requestId
    : undefined;

  const submit = form.handleSubmit(async (values) => {
    if (
      submitInFlight.current ||
      creationIntent.current !== null ||
      createMutation.isPending ||
      createMutation.isSuccess
    )
      return;
    if (
      !verifiedFacilityId ||
      !verifiedFacility ||
      verifiedFacility.facility_id !== verifiedFacilityId ||
      coachQuery.isPending ||
      coachQuery.isFetching
    ) {
      return;
    }

    const timezone = verifiedFacility.timezone || "UTC";
    const effectiveEndDate =
      values.recurrence === "once" ? values.start_date : values.end_date;
    const scheduleStart = sessionInstants(
      values.start_date,
      values.session_time,
      Number(values.schedule_duration_minutes),
      timezone,
    );
    const scheduleEnd = sessionInstants(
      effectiveEndDate,
      values.session_time,
      Number(values.schedule_duration_minutes),
      timezone,
    );
    if (scheduleStart === null || scheduleEnd === null) {
      form.setError("start_date", { message: "Start date is required." });
      return;
    }

    const selectedInstructor =
      !coachQuery.isError &&
      values.coach_profile_id &&
      coachQuery.data?.some(
        (coach) => coach.coach_profile_id === values.coach_profile_id,
      )
        ? values.coach_profile_id
        : null;

    const body: UnifiedProgramCreate = {
      name: values.name,
      program_type: values.program_type,
      description: values.description.trim() || null,
      facility_id: verifiedFacilityId,
      payee_type: "facility",
      payee_id: verifiedFacilityId,
      start_date: scheduleStart.start.toISOString(),
      end_date: scheduleEnd.end.toISOString(),
      schedule_rrules: [recurrenceRule(values.recurrence, scheduleEnd.end)],
      schedule_dtstart: scheduleStart.start.toISOString(),
      schedule_duration_minutes: Number(values.schedule_duration_minutes),
      timezone,
      coach_profile_id: selectedInstructor,
      learning_space_enabled: values.learning_space_enabled,
      learning_space_post_permission: "all_learners",
      tracks: [],
    };

    submitInFlight.current = true;
    let owned: CreationIntent | null = null;
    try {
      // Set synchronously before dispatch. Mutation/React success rendering is
      // not the exclusion: the same intent survives finally and background reads.
      owned = { phase: "dispatched" };
      creationIntent.current = owned;
      if (mounted.current) setCreationPhase(owned.phase);
      const created = await createMutation.mutateAsync(body);
      const valid =
        created !== null &&
        typeof created === "object" &&
        sameProgramFacility(created.facility_id, body.facility_id) &&
        isProgramAuthoringLocator(created.program_id);
      holdCreation(owned, valid ? "confirmed" : "uncertain");
      if (valid && mounted.current) navigate(`/programs/${created.program_id}`);
    } catch (error) {
      if (
        owned !== null &&
        creationIntent.current === owned &&
        owned.phase === "dispatched"
      ) {
        if (confirmedNoEffectRejection(error)) {
          creationIntent.current = null;
          if (mounted.current) setCreationPhase(null);
        } else holdCreation(owned, "uncertain");
      }
    } finally {
      submitInFlight.current = false;
    }
  });

  if (facilityLoading) {
    return (
      <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Create Program
        </Typography>
        <LoadingStatus label="Loading facility…" />
      </Stack>
    );
  }

  if (!verifiedFacilityId || !verifiedFacility) {
    return (
      <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Create Program
        </Typography>
        <Alert severity="info">
          Select an available facility before creating a program.
        </Alert>
      </Stack>
    );
  }

  const recurrence = form.watch("recurrence");
  const oneOffPreview =
    recurrence === "once"
      ? sessionInstants(
          form.watch("start_date"),
          form.watch("session_time"),
          Number(form.watch("schedule_duration_minutes")),
          verifiedFacility.timezone || "UTC",
        )
      : null;

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Box>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Create Program
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {verifiedFacility.name}
        </Typography>
      </Box>

      {creationPhase === "uncertain" ? (
        <Alert severity="warning">
          The creation outcome is unconfirmed. Open Programs to check for the
          result before creating again. Rechecking creation access does not
          resolve this request.
        </Alert>
      ) : creationPhase === "confirmed" ? (
        <Alert severity="info">
          Check Programs for the result of this request before creating another
          program.
        </Alert>
      ) : null}

      <form noValidate onSubmit={submit}>
        <Stack spacing={3} sx={{ width: "100%", maxWidth: 760 }}>
          {createMutation.isError && creationPhase === null ? (
            <Alert severity="error">
              <Stack spacing={0.25}>
                <span>Program could not be created. Please try again.</span>
                {createRequestId ? (
                  <Typography variant="caption" color="inherit">
                    Support reference: {createRequestId}
                  </Typography>
                ) : null}
              </Stack>
            </Alert>
          ) : null}

          <Stack spacing={2}>
            <Typography
              component="h2"
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
            >
              Program
            </Typography>

            <TextField
              {...form.register("name")}
              id="p-name"
              label="Program name"
              size="small"
              fullWidth
              disabled={isCreating}
              error={Boolean(form.formState.errors.name)}
              helperText={form.formState.errors.name?.message}
              slotProps={{ htmlInput: { maxLength: 255 } }}
              placeholder="e.g. Junior Group Training"
            />

            <Controller
              name="program_type"
              control={form.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="p-type"
                  label="Program type"
                  select
                  size="small"
                  fullWidth
                  disabled={isCreating}
                >
                  {PROGRAM_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {PROGRAM_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <TextField
              {...form.register("description")}
              id="p-desc"
              label="Description"
              size="small"
              fullWidth
              multiline
              rows={3}
              disabled={isCreating}
              placeholder="What this program covers and who it is for"
            />

            <Controller
              name="learning_space_enabled"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      disabled={isCreating}
                    />
                  }
                  label="Include a private Learning Space for full-course and join-late learners"
                />
              )}
            />
            {form.watch("learning_space_enabled") ? (
              <Typography variant="body2" color="text.secondary">
                Learners can access course discussion and resources. Posting
                becomes read-only 30 days after the final session; drop-ins do
                not receive access.
              </Typography>
            ) : null}

            <Box>
              {coachQuery.isPending ? (
                <LoadingStatus label="Loading active coaches…" />
              ) : coachQuery.isError ? (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      type="button"
                      color="inherit"
                      size="small"
                      startIcon={<RefreshRoundedIcon />}
                      disabled={coachQuery.isFetching || isCreating}
                      onClick={() => {
                        void coachQuery.refetch();
                      }}
                    >
                      Retry coaches
                    </Button>
                  }
                >
                  <Stack spacing={0.25}>
                    <span>Active coaches could not be loaded.</span>
                    {coachRequestId ? (
                      <Typography variant="caption" color="inherit">
                        Support reference: {coachRequestId}
                      </Typography>
                    ) : null}
                  </Stack>
                </Alert>
              ) : coachOptions.length === 1 ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No active coaches at this facility.
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    disabled={isCreating}
                    onClick={() => navigate("/staff")}
                  >
                    Add coach
                  </Button>
                </Stack>
              ) : (
                <Controller
                  name="coach_profile_id"
                  control={form.control}
                  render={({ field }) => (
                    <Autocomplete
                      id="p-instructor"
                      options={coachOptions}
                      value={
                        coachOptions.find(
                          (option) => option.profileId === field.value,
                        ) ?? NO_INSTRUCTOR_OPTION
                      }
                      getOptionLabel={(option) => option.label}
                      getOptionKey={(option) =>
                        option.profileId || "no-instructor"
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.profileId === value.profileId
                      }
                      disableClearable
                      disabled={isCreating}
                      onChange={(_event, option) => {
                        field.onChange(option.profileId);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Instructor"
                          size="small"
                        />
                      )}
                    />
                  )}
                />
              )}
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography
              component="h2"
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
            >
              Schedule
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                {...form.register("start_date")}
                id="p-start"
                label="Start date"
                type="date"
                size="small"
                fullWidth
                disabled={isCreating}
                error={Boolean(form.formState.errors.start_date)}
                helperText={form.formState.errors.start_date?.message}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              {recurrence === "once" ? (
                <Stack spacing={0.25} sx={{ alignSelf: "center", minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    One session
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {oneOffPreview
                      ? formatTimeRange(
                          oneOffPreview.start.toISOString(),
                          oneOffPreview.end.toISOString(),
                          verifiedFacility.timezone || "UTC",
                        )
                      : "The end is calculated from the start time and session length."}
                  </Typography>
                </Stack>
              ) : (
                <TextField
                  {...form.register("end_date")}
                  id="p-end"
                  label="End date"
                  type="date"
                  size="small"
                  fullWidth
                  disabled={isCreating}
                  error={Boolean(form.formState.errors.end_date)}
                  helperText={form.formState.errors.end_date?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Controller
                name="recurrence"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="p-recurrence"
                    label="Repeats"
                    select
                    size="small"
                    fullWidth
                    disabled={isCreating}
                  >
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="once">Does not repeat</MenuItem>
                  </TextField>
                )}
              />
              <TextField
                {...form.register("session_time")}
                id="p-session-time"
                label="Session start time"
                type="time"
                size="small"
                fullWidth
                disabled={isCreating}
                error={Boolean(form.formState.errors.session_time)}
                helperText={
                  form.formState.errors.session_time?.message ??
                  `In ${verifiedFacility.timezone || "UTC"}`
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>

            <TextField
              {...form.register("schedule_duration_minutes")}
              id="p-duration"
              label="Session length (minutes)"
              type="number"
              size="small"
              disabled={isCreating}
              error={Boolean(form.formState.errors.schedule_duration_minutes)}
              helperText={
                form.formState.errors.schedule_duration_minutes?.message
              }
              slotProps={{ htmlInput: { min: 1, max: 480 } }}
              sx={{ width: { xs: "100%", sm: 280 } }}
            />
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ alignItems: { xs: "stretch", sm: "center" } }}
          >
            <Button
              type="submit"
              size="small"
              variant="contained"
              disabled={
                isCreating ||
                coachReadInProgress ||
                creationPhase !== null ||
                createMutation.isSuccess
              }
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {isCreating ? "Creating…" : "Create program"}
            </Button>
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={isCreating}
              onClick={() => navigate("/programs")}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {creationPhase === "confirmed" || creationPhase === "uncertain"
                ? "Open Programs"
                : "Cancel"}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
